import React, { useRef, useEffect, useState, useMemo, Suspense, useCallback } from 'react';
import { useGLTF, TransformControls } from '@react-three/drei';
import { ThreeEvent, useThree } from '@react-three/fiber';
import { SceneState, useSceneStore } from '../../state/sceneStore';
import { Group } from 'three';
import type { Sensor } from '../../api/models';
import type { TransformControls as TransformControlsImpl } from 'three-stdlib';
import AlignmentPointCloud from './AlignmentPointCloud';
import {
  shouldShowSensorStatusSphere,
  statusSphereColor,
} from '../../utils/sensorStatusSphere';

/** Matches backend SENSOR_EULER_ORDER ('xyz'): yaw about global Z, outermost. */
const SENSOR_EULER_ORDER = 'ZYX' as const;

interface ModelProps {
  type: string;
}

const GltfModel: React.FC<ModelProps> = ({ type }) => {
  const modelPath = `/models/${type.toLowerCase()}-sensor.glb`;
  const { scene } = useGLTF(modelPath);

  const copiedScene = useMemo(() => scene.clone(), [scene]);

  const rotation: [number, number, number] = [Math.PI / 2, 0, 0];
  if (type.toLowerCase() === 'dome') {
    rotation[2] = Math.PI;
  }

  return (
    <primitive
      object={copiedScene}
      rotation={rotation}
      scale={[0.005, 0.005, 0.005]}
    />
  );
};

const CylinderModel: React.FC = () => (
  <mesh rotation={[Math.PI / 2, 0, 0]}>
    <cylinderGeometry args={[0.25, 0.25, 0.5, 32]} />
    <meshStandardMaterial color={'black'} />
  </mesh>
);

const CuboidModel: React.FC = () => (
  <mesh position={[0, 0, 0.25]}>
    <boxGeometry args={[0.5, 0.5, 0.5]} />
    <meshStandardMaterial color={'black'} />
  </mesh>
);

const SensorModel: React.FC<ModelProps> = ({ type }) => {
  const availableModels = ['hd', 'dome', 'lr', 'm1', 'new dome'];
  const modelType = type.toLowerCase();

  // Map m1 to lr model, and new dome to dome model since they share physical appearance
  const effectiveType = modelType === 'm1' ? 'lr' : modelType === 'new dome' ? 'dome' : modelType;

  if (availableModels.includes(modelType)) {
    return (
      <Suspense fallback={<CylinderModel />}>
        <GltfModel type={effectiveType} />
      </Suspense>
    );
  }

  if (modelType === 'mems') {
    return <CuboidModel />;
  }

  return <CylinderModel />;
};

useGLTF.preload('/models/hd-sensor.glb');
useGLTF.preload('/models/dome-sensor.glb');
useGLTF.preload('/models/lr-sensor.glb');

interface LidarSensorInstanceProps {
  id: string;
}

const LidarSensorInstance: React.FC<LidarSensorInstanceProps> = ({ id }) => {
  const sensor = useSceneStore((state: SceneState) =>
    state.sensors.find((s) => s.id === id)
  );
  const selectedObjectId = useSceneStore(
    (state: SceneState) => state.selectedObjectId
  );
  const setSelectedObjectId = useSceneStore(
    (state: SceneState) => state.setSelectedObjectId
  );
  const updateSensor = useSceneStore((state: SceneState) => state.updateSensor);
  const pushSensorPoseHistory = useSceneStore((state: SceneState) => state.pushSensorPoseHistory);
  const setIsDragging = useSceneStore((state: SceneState) => state.setIsDragging);
  const isDragging = useSceneStore((state: SceneState) => state.isDragging);
  const isSelected = selectedObjectId === id;
  const transformMode = useSceneStore((state: SceneState) => state.transformMode);
  const isAlignmentMode = useSceneStore((state: SceneState) => state.isAlignmentMode);
  const pointCloudVisible = useSceneStore((state: SceneState) => state.pointCloudVisible);
  const recommendedNextId = useSceneStore(
    (state: SceneState) => state.alignmentConnectivity?.recommended_next?.sensor_id
  );
  const isRecommendedNext = isAlignmentMode && recommendedNextId === id;
  const controlsRef = useRef<TransformControlsImpl>(null);
  const isLocallyDraggingRef = useRef(false);
  const dragHistoryPushedRef = useRef(false);
  const ignoreSelectionClickRef = useRef(false);
  const sensorRef = useRef(sensor);
  sensorRef.current = sensor;
  const [target, setTarget] = useState<Group | null>(null);
  const { camera } = useThree();
  const sensorPosition = sensor?.position;
  const sensorRotation = sensor?.rotation;

  const applyAlignmentConstraints = (group: Group) => {
    if (group.position.z < 0) {
      group.position.z = 0;
    }
    const currentSensor = sensorRef.current;
    const { isAlignmentMode: aligning, transformMode: mode } = useSceneStore.getState();
    if (aligning && currentSensor?.floorLocked && mode === 'rotate') {
      group.rotation.x = 0;
      group.rotation.y = 0;
    }
  };

  const commitTargetPose = (group: Group) => {
    const currentSensor = sensorRef.current;
    if (!currentSensor) {
      return;
    }
    applyAlignmentConstraints(group);
    const updates: Partial<Sensor> = {
      position: [group.position.x, group.position.y, group.position.z] as [number, number, number],
      rotation: [group.rotation.x, group.rotation.y, group.rotation.z] as [number, number, number],
    };
    updateSensor(id, updates);
  };

  const handleSensorSelect = useCallback((event: ThreeEvent<MouseEvent>) => {
    event.stopPropagation();
    if (ignoreSelectionClickRef.current || useSceneStore.getState().isDragging) {
      return;
    }
    setSelectedObjectId(id);
  }, [id, setSelectedObjectId]);

  const disableDecorationRaycast = useCallback(() => null, []);

  useEffect(() => {
    if (!sensorPosition || !sensorRotation || !target || isLocallyDraggingRef.current) {
      return;
    }
    target.rotation.order = SENSOR_EULER_ORDER;
    target.position.set(sensorPosition[0], sensorPosition[1], sensorPosition[2]);
    target.rotation.set(sensorRotation[0], sensorRotation[1], sensorRotation[2]);
  }, [target, sensorPosition, sensorRotation, isDragging]);

  useEffect(() => {
    if (!target) {
      return;
    }
    target.rotation.order = SENSOR_EULER_ORDER;
  }, [target]);

  useEffect(() => {
    const controls = controlsRef.current;
    if (controls) {
      const callback = (event: { value: boolean }) => {
        const dragging = event.value;
        isLocallyDraggingRef.current = dragging;
        ignoreSelectionClickRef.current = dragging;
        setIsDragging(dragging);

        // Immediately disable OrbitControls when dragging starts
        if (dragging) {
          if (!dragHistoryPushedRef.current) {
            pushSensorPoseHistory(id);
            dragHistoryPushedRef.current = true;
          }
          const canvas = document.querySelector('canvas');
          if (canvas) {
            canvas.style.pointerEvents = 'none';
          }

          // Directly disable OrbitControls immediately
          const orbitControls = document.querySelector('canvas')?.parentElement?.querySelector('[data-orbit-controls]');
          if (orbitControls) {
            (orbitControls as any).enabled = false;
          }
        } else {
          dragHistoryPushedRef.current = false;
          // Re-enable pointer events when dragging stops
          const canvas = document.querySelector('canvas');
          if (canvas) {
            canvas.style.pointerEvents = 'auto';
          }
          if (target) {
            commitTargetPose(target);
          }
          window.setTimeout(() => {
            ignoreSelectionClickRef.current = false;
          }, 150);
        }
      };
      (controls as any).addEventListener('dragging-changed', callback);
      return () =>
        (controls as any).removeEventListener('dragging-changed', callback);
    }
  }, [target, setIsDragging, isSelected, camera, id, pushSensorPoseHistory]);

  if (!sensor) {
    return null;
  }

  const showTransformControls =
    isSelected &&
    target &&
    ((!isAlignmentMode) || (isAlignmentMode && Boolean(sensor.pcdUrl)));

  return (
    <>
      <group ref={setTarget}>
        <group onClick={handleSensorSelect}>
          <SensorModel type={sensor.type} />
        </group>
        {isRecommendedNext && (
          <mesh
            rotation={[Math.PI / 2, 0, 0]}
            position={[0, 0, 0.05]}
            raycast={disableDecorationRaycast}
          >
            <ringGeometry args={[0.35, 0.55, 32]} />
            <meshBasicMaterial color="#38bdf8" transparent opacity={0.9} depthTest={false} />
          </mesh>
        )}
        {shouldShowSensorStatusSphere(isAlignmentMode) && (
          <mesh
            position={[0, 0, 0.8]}
            raycast={disableDecorationRaycast}
          >
            <sphereGeometry args={[0.12, 16, 16]} />
            <meshBasicMaterial
              color={statusSphereColor(sensor)}
              depthTest={false}
              transparent
              opacity={0.95}
            />
          </mesh>
        )}
        {isAlignmentMode &&
          pointCloudVisible &&
          sensor.pcdUrl &&
          (sensor.alignmentStatus === 'aligned' || isSelected) && (
          <AlignmentPointCloud
            url={sensor.pcdUrl}
            sensorId={id}
            isActiveAlignment={isSelected}
          />
        )}
      </group>
      {showTransformControls && (
        <TransformControls
          mode={transformMode}
          space={transformMode === 'rotate' ? 'local' : 'world'}
          object={target}
          ref={controlsRef}
          onChange={() => {
            if (!target || !isLocallyDraggingRef.current) {
              return;
            }
            applyAlignmentConstraints(target);
          }}
        />
      )}
    </>
  );
};

const LidarSensor: React.FC = () => {
  const sensors = useSceneStore((state: SceneState) => state.sensors);

  return (
    <>
      {sensors.map((sensor: Sensor) => (
        <LidarSensorInstance key={sensor.id} id={sensor.id} />
      ))}
    </>
  );
};

export default LidarSensor;
